import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { parseAbbrev } from "@/lib/format";
import { previewScore, decisionFromScore } from "@/lib/scoring";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  UploadCloud, Loader2, Trash2, ImageDown, X, ListChecks, Sparkles, CalendarClock,
} from "lucide-react";
import ExtractionOverlay from "@/components/ExtractionOverlay";
import { Switch } from "@/components/ui/switch";
import { DateTimeControl, todayIso, nowHM, combineDateHM } from "@/lib/datetime";
import BatchImport from "@/pages/BatchImport";

const NEWS_OPTIONS = [
  { value: "none", label: "Aucune" },
  { value: "earnings", label: "Résultats trimestriels" },
  { value: "major_contract", label: "Contrat majeur" },
  { value: "ma", label: "Fusion / acquisition" },
  { value: "fda", label: "Annonce FDA" },
  { value: "fundraising", label: "Levée de fonds (dilutive)" },
  { value: "dilution", label: "Dilution récente" },
];

// -----------------------------------------------------------------------------
// Field primitives
// -----------------------------------------------------------------------------
function Field({ label, hint, required, children, highlight }) {
  return (
    <div className={highlight ? "fade-in-up" : ""}>
      <Label className="text-[10px] uppercase tracking-widest text-muted-1">
        {label} {required && <span className="text-ignore">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-[10px] text-muted-2 mt-1">{hint}</div>}
    </div>
  );
}

function NumberInput({ value, onChange, placeholder, testId, highlight }) {
  return (
    <Input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className={`h-10 bg-input border-line-strong font-mono-num ${highlight ? "border-invest/60" : ""}`}
    />
  );
}

// -----------------------------------------------------------------------------
// SingleStock: manual form with inline "pré-remplir depuis capture"
// -----------------------------------------------------------------------------
const EMPTY = {
  symbol: "", name: "", rank_in_list: "",
  volume: "", avg_volume_3m: "",
  shares_outstanding: "", open_price: "", prev_close: "",
  pct_range: "", market_cap: "", pct_turnover: "",
  news_category: "none",
  premarket_percent: "",
  current_price: "",   // captured price at import moment (iter11)
};

function SingleStockForm({ onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [isReal, setIsReal] = useState(false); // iter14: user chooses Test/Real up-front
  // Date/time of the measurement (default = now)
  const _initHM = nowHM();
  const [dt, setDt] = useState({ date: todayIso(), hour: _initHM.h, minute: _initHM.m });

  // Iter 27 — day strategy (Sniper/Filet). Loaded from /day-state; locked once
  // the first stock of the day is saved.
  const [dayStrategy, setDayStrategy] = useState("sniper");
  const [strategyLocked, setStrategyLocked] = useState(false);

  useEffect(() => {
    if (!dt.date) return;
    let cancelled = false;
    api.get(`/day-state/${dt.date}`).then((r) => {
      if (cancelled) return;
      setDayStrategy(r.data?.strategy || "sniper");
      setStrategyLocked(!!r.data?.locked);
    }).catch(() => { /* keep default */ });
    return () => { cancelled = true; };
  }, [dt.date]);

  const changeStrategy = async (next) => {
    if (strategyLocked) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `La stratégie de ce jour est verrouillée sur ${dayStrategy.toUpperCase()}.\n\n` +
        `La changer pour ${next.toUpperCase()} recalcule les allocations pour toutes ` +
        `les actions déjà ajoutées ce jour-là ainsi que les futures.\n\nConfirmer ?`
      );
      if (!ok) return;
    }
    try {
      await api.patch(`/day-state/${dt.date}`, { strategy: next });
      setDayStrategy(next);
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    }
  };

  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importedFields, setImportedFields] = useState(new Set());
  const [imagePreview, setImagePreview] = useState(null);
  const [newsSuggestion, setNewsSuggestion] = useState(null);
  const fileRef = useRef(null);

  const setField = (k) => (v) => setF((prev) => ({ ...prev, [k]: v?.target ? v.target.value : v }));

  const criteria = {
    volume: parseAbbrev(f.volume),
    avg_volume_3m: parseAbbrev(f.avg_volume_3m),
    shares_outstanding: parseAbbrev(f.shares_outstanding),
    open_price: parseAbbrev(f.open_price),
    prev_close: parseAbbrev(f.prev_close),
    pct_range: parseAbbrev(f.pct_range),
    market_cap: parseAbbrev(f.market_cap),
    pct_turnover: parseAbbrev(f.pct_turnover),
    news_category: f.news_category,
  };
  const preview = previewScore(criteria);
  const dec = decisionFromScore(preview.total);
  const decColor = dec.color === "fonce" ? "var(--invest)"
    : dec.color === "investis" ? "var(--monitor)" : "var(--ignore)";

  const pickFile = (file) => {
    if (!file) return;
    setImportFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const runExtract = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await api.post("/vision/extract-detail", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = r.data.detail || {};
      const filled = new Set();
      const next = { ...f };
      const mapping = {
        symbol: "symbol", name: "name",
        volume: "volume", avg_volume_3m: "avg_volume_3m",
        shares_outstanding: "shares_outstanding",
        open_price: "open_price", prev_close: "prev_close",
        pct_range: "pct_range", market_cap: "market_cap",
        pct_turnover: "pct_turnover",
        news_category: "news_category",
        percent_change: "premarket_percent",
        price: "current_price",   // extracted big price on the card (iter11)
      };
      for (const [src, dst] of Object.entries(mapping)) {
        if (d[src] !== null && d[src] !== undefined && d[src] !== "") {
          next[dst] = typeof d[src] === "number" ? String(d[src]) : d[src];
          filled.add(dst);
        }
      }
      setF(next);
      setImportedFields(filled);
      // News suggestion from AI second-pass
      if (d.news_suggestion && d.news_suggestion.category) {
        setNewsSuggestion(d.news_suggestion);
      } else {
        setNewsSuggestion(null);
      }
      toast.success("Extraction réussie", {
        description: `${filled.size} champ(s) pré-rempli(s). Vérifiez et corrigez si besoin.`,
      });
    } catch (e) {
      toast.error("Extraction échouée", { description: e?.response?.data?.detail || String(e) });
    } finally {
      setImporting(false);
    }
  };

  const clearImport = () => {
    setImportFile(null); setImagePreview(null); setImportedFields(new Set());
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = async () => {
    if (!f.symbol) { toast.error("Symbole requis"); return; }
    setSaving(true);
    try {
      const isoTs = combineDateHM(dt.date, dt.hour, dt.minute);
      const body = {
        symbol: f.symbol,
        name: f.name || null,
        trade_date: dt.date,
        rank_in_list: (f.rank_in_list !== "" && f.rank_in_list != null)
          ? parseInt(f.rank_in_list, 10)
          : null,
        criteria,
        premarket: f.premarket_percent !== "" && f.premarket_percent !== null
          ? { percent: parseAbbrev(f.premarket_percent), timestamp: isoTs }
          : { percent: null, timestamp: isoTs },
        // iter11: capture price + auto-fill entry_price for Test stocks
        status: isReal ? "real" : "test",
        capture_price: f.current_price !== "" && f.current_price != null
          ? parseAbbrev(f.current_price) : null,
        entry_price: f.current_price !== "" && f.current_price != null
          ? parseAbbrev(f.current_price) : null,
      };
      try {
        const r = await api.post("/stocks", body);
        toast.success("Action enregistrée", { description: `${r.data.symbol} — score ${r.data.score}` });
        onSaved?.(r.data);
      } catch (err) {
        if (err?.response?.status === 409) {
          const detail = err.response.data?.detail || {};
          const ok = window.confirm(
            `${f.symbol} existe déjà pour cette journée. Mettre à jour l'entrée existante avec ces nouvelles valeurs ?`
          );
          if (!ok) return;
          const r2 = await api.post("/stocks?force_update=true", body);
          toast.success("Action mise à jour", { description: `${r2.data.symbol} — score ${r2.data.score}` });
          onSaved?.(r2.data);
        } else {
          throw err;
        }
      }
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    } finally {
      setSaving(false);
    }
  };

  const hi = (k) => importedFields.has(k);

  return (
    <div className="grid md:grid-cols-[1fr_320px] gap-6">
      {/* LEFT: form */}
      <div className="space-y-6">
        {/* Status toggle — first choice per iter14 spec */}
        <div className="bg-card-1 border border-line rounded-xl px-6 py-4 flex items-center gap-4">
          <Switch
            checked={isReal}
            onCheckedChange={setIsReal}
            className="data-[state=unchecked]:bg-white data-[state=unchecked]:border-neutral-300"
            data-testid="new-stock-status-toggle"
          />
          <div className="flex-1">
            <div className="text-sm font-medium">
              {isReal ? "Réel — argent réellement investi" : "Test — simulation (par défaut)"}
            </div>
            <div className="text-[11px] text-muted-1 mt-0.5">
              {isReal
                ? "Ce titre alimentera le capital réel, la réserve et le drawdown."
                : "Ce titre servira pour le score et l'apprentissage, sans toucher au capital réel."}
            </div>
          </div>
          <span className={`text-[10px] font-mono-num uppercase tracking-widest px-2 py-0.5 rounded-full border ${isReal ? "text-[#00E676] border-[#00E676]/30 bg-[#00E676]/10" : "text-[#FFCC00] border-[#FFCC00]/30 bg-[#FFCC00]/10"}`}>
            {isReal ? "Réel" : "Test"}
          </span>
        </div>

        {/* Iter 27 — Day strategy (Sniper / Filet), locked once first stock is saved */}
        <div className="bg-card-1 border border-line rounded-xl px-6 py-4 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <div className="text-sm font-medium">
              Stratégie de la journée
              {strategyLocked && (
                <span className="ml-2 text-[10px] font-mono-num uppercase tracking-widest text-muted-2">verrouillée</span>
              )}
            </div>
            <div className="text-[11px] text-muted-1 mt-0.5">
              {dayStrategy === "filet"
                ? "Filet — équi-pondéré, filtre min. (cap ≥ 5M$, volume ≥ 500k)."
                : "Sniper — allocation pondérée par score, seuils Fonce/Investis/Ignore."}
            </div>
          </div>
          <div className="flex gap-1 rounded-full bg-input p-1 border border-line-strong">
            {["sniper", "filet"].map((opt) => (
              <button
                key={opt}
                onClick={() => opt !== dayStrategy && changeStrategy(opt)}
                className={`px-3 py-1 rounded-full text-[11px] font-medium uppercase tracking-widest transition-colors ${
                  dayStrategy === opt
                    ? (opt === "filet" ? "text-[#0A0A0A]" : "text-[#0A0A0A]")
                    : "text-muted-1 hover:text-app"
                }`}
                style={dayStrategy === opt
                  ? { background: opt === "filet" ? "#00B0FF" : "#00E676" }
                  : undefined}
                data-testid={`new-stock-strategy-${opt}`}
              >{opt}</button>
            ))}
          </div>
        </div>

        {/* iter 28.7 — gentle nudge toward batch import on a Filet day */}
        {dayStrategy === "filet" && (
          <div
            className="bg-invest/5 border border-invest/30 rounded-xl px-4 py-3 flex items-start gap-3 text-[12px] leading-snug"
            data-testid="filet-batch-nudge"
          >
            <div className="text-invest text-lg leading-none">💡</div>
            <div className="flex-1">
              <span className="text-app font-medium">Filet est conçu pour évaluer une liste complète.</span>
              <span className="text-muted-1"> Tu peux continuer un titre à la fois, mais l&apos;import groupé est plus rapide et plus représentatif.</span>
              <button
                type="button"
                onClick={() => document.querySelector('[data-testid="mode-bulk"]')?.click()}
                className="ml-2 inline-flex items-center gap-1 text-invest underline decoration-invest/40 hover:decoration-invest whitespace-nowrap"
                data-testid="filet-batch-nudge-link"
              >
                Importer la liste →
              </button>
            </div>
          </div>
        )}

        {/* Import panel — fused with the form */}
        <div className="bg-card-1 border border-line rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-line">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-invest" />
              <div className="font-display text-sm uppercase tracking-widest">Pré-remplir depuis une capture</div>
            </div>
            {importFile && !importing && (
              <button
                onClick={clearImport}
                className="text-[11px] text-muted-1 hover:text-app transition-colors flex items-center gap-1"
                data-testid="import-clear-btn"
              >
                <X size={12} /> retirer l'image
              </button>
            )}
          </div>

          <div className="p-6">
            {!importFile ? (
              <label
                htmlFor="importfile"
                className="block border border-dashed border-line-strong rounded-lg p-8 text-center cursor-pointer hover:bg-card-2 transition-colors"
                data-testid="import-dropzone"
              >
                <input
                  id="importfile"
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => pickFile(e.target.files?.[0])}
                  data-testid="import-file-input"
                />
                <ImageDown size={22} className="mx-auto mb-3 text-muted-1" />
                <div className="text-sm">Déposez une capture de fiche Webull</div>
                <div className="text-[11px] text-muted-2 mt-1">
                  Gemini 3 Flash extraira automatiquement les 7 critères. Vous validerez ensuite.
                </div>
              </label>
            ) : (
              <div className="space-y-4">
                {/* Big image container with overlay ANIMATION INSIDE */}
                <div className="relative w-full h-64 rounded-lg overflow-hidden border border-line-strong bg-input">
                  <img src={imagePreview} alt="capture" className="w-full h-full object-contain" />
                  <ExtractionOverlay open={importing} />
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs font-mono-num text-muted-1 truncate">{importFile.name}</div>
                  <Button
                    onClick={runExtract}
                    disabled={importing}
                    className="h-10 btn-primary font-medium"
                    data-testid="import-extract-btn"
                  >
                    {importing ? (
                      <><Loader2 size={14} className="mr-2 animate-spin" /> Extraction…</>
                    ) : (
                      <><Sparkles size={14} className="mr-2" /> Lancer l'extraction</>
                    )}
                  </Button>
                </div>
                {importedFields.size > 0 && (
                  <div className="text-[11px] text-invest font-mono-num">
                    {importedFields.size} champ(s) pré-remplis — vérifiez ci-dessous.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Journée & heure */}
        <div className="bg-card-1 border border-line rounded-xl p-6 space-y-3">
          <div className="flex items-center gap-2">
            <CalendarClock size={14} className="text-muted-1" />
            <h3 className="font-display text-sm text-muted-1 uppercase tracking-widest">Journée & heure de la lecture</h3>
          </div>
          <DateTimeControl
            date={dt.date}
            hour={dt.hour}
            minute={dt.minute}
            onChange={setDt}
            testIdPrefix="add-datetime"
          />
          <div className="text-[10px] text-muted-2">
            Par défaut : aujourd'hui à l'heure actuelle. Modifiable si vous saisissez une lecture rétroactive.
          </div>
        </div>

        {/* Identification */}
        <div className="bg-card-1 border border-line rounded-xl p-6 space-y-4">
          <h3 className="font-display text-sm text-muted-1 uppercase tracking-widest">Identification</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Symbole" required highlight={hi("symbol")}>
              <Input
                value={f.symbol}
                onChange={(e) => setField("symbol")(e.target.value.toUpperCase())}
                placeholder="DFNS"
                data-testid="in-symbol"
                className={`uppercase h-10 bg-input border-line-strong ${hi("symbol") ? "border-invest/60" : ""}`}
              />
            </Field>
            <Field label="Nom" highlight={hi("name")}>
              <Input
                value={f.name}
                onChange={setField("name")}
                placeholder="T3 Defense Inc"
                data-testid="in-name"
                className={`h-10 bg-input border-line-strong ${hi("name") ? "border-invest/60" : ""}`}
              />
            </Field>
            <Field label="Rang dans le screener" hint="Optionnel — 1 = tout en haut. Auto-attribué si laissé vide.">
              <NumberInput value={f.rank_in_list} onChange={setField("rank_in_list")} placeholder="auto" testId="in-rank" />
            </Field>
            <Field label="% Pré-market (optionnel)" highlight={hi("premarket_percent")}>
              <NumberInput value={f.premarket_percent} onChange={setField("premarket_percent")} placeholder="170.69" testId="in-premarket" highlight={hi("premarket_percent")} />
            </Field>
            <Field label="Prix courant $ (moment de la capture)" hint="Auto-rempli comme prix d'entrée en mode Test" highlight={hi("current_price")}>
              <NumberInput value={f.current_price} onChange={setField("current_price")} placeholder="11.78" testId="in-current-price" highlight={hi("current_price")} />
            </Field>
          </div>
        </div>

        {/* Scoring criteria */}
        <div className="bg-card-1 border border-line rounded-xl p-6 space-y-4">
          <h3 className="font-display text-sm text-muted-1 uppercase tracking-widest">Critères de scoring</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Volume" hint="Accepte K/M/B (ex: 53.72M)" highlight={hi("volume")}>
              <NumberInput value={f.volume} onChange={setField("volume")} placeholder="53.72M" testId="in-volume" highlight={hi("volume")} />
            </Field>
            <Field label="Volume moyen (3 mois)" hint="Ex: 584.46K" highlight={hi("avg_volume_3m")}>
              <NumberInput value={f.avg_volume_3m} onChange={setField("avg_volume_3m")} placeholder="584.46K" testId="in-avg-vol" highlight={hi("avg_volume_3m")} />
            </Field>
            <Field label="Actions en circulation" hint="Ex: 1.01M" highlight={hi("shares_outstanding")}>
              <NumberInput value={f.shares_outstanding} onChange={setField("shares_outstanding")} placeholder="1.01M" testId="in-shares" highlight={hi("shares_outstanding")} />
            </Field>
            <Field label="Capitalisation boursière" hint="Ex: 11.9M" highlight={hi("market_cap")}>
              <NumberInput value={f.market_cap} onChange={setField("market_cap")} placeholder="11.9M" testId="in-mcap" highlight={hi("market_cap")} />
            </Field>
            <Field label="Prix d'ouverture" hint="Sert au calcul du gap" highlight={hi("open_price")}>
              <NumberInput value={f.open_price} onChange={setField("open_price")} placeholder="6.90" testId="in-open" highlight={hi("open_price")} />
            </Field>
            <Field label="Clôture précédente" highlight={hi("prev_close")}>
              <NumberInput value={f.prev_close} onChange={setField("prev_close")} placeholder="4.35" testId="in-prev-close" highlight={hi("prev_close")} />
            </Field>
            <Field label="% de la fourchette (Webull direct)" hint="Ne pas recalculer" highlight={hi("pct_range")}>
              <NumberInput value={f.pct_range} onChange={setField("pct_range")} placeholder="137.70" testId="in-range" highlight={hi("pct_range")} />
            </Field>
            <Field label="% de roulement (direct)" hint="Peut dépasser 1000%" highlight={hi("pct_turnover")}>
              <NumberInput value={f.pct_turnover} onChange={setField("pct_turnover")} placeholder="5315.92" testId="in-turnover" highlight={hi("pct_turnover")} />
            </Field>
            <div className="col-span-2">
              <Field label="Catégorie de nouvelles">
                <Select value={f.news_category} onValueChange={setField("news_category")}>
                  <SelectTrigger data-testid="in-news" className="h-10 bg-input border-line-strong">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NEWS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {newsSuggestion && newsSuggestion.category && newsSuggestion.category !== "none" && (
                <div className="mt-2 flex items-start gap-2 bg-invest/8 border border-invest/25 rounded-lg p-3" data-testid="news-suggestion-card">
                  <Sparkles size={14} className="text-invest mt-0.5" />
                  <div className="flex-1 text-xs">
                    <div className="text-app">
                      L'IA suggère : <b>{NEWS_OPTIONS.find((o) => o.value === newsSuggestion.category)?.label || newsSuggestion.category}</b>
                      {newsSuggestion.confidence && <span className="ml-1 text-muted-1">(confiance {newsSuggestion.confidence})</span>}
                    </div>
                    {newsSuggestion.reason && <div className="text-muted-1 mt-0.5">{newsSuggestion.reason}</div>}
                    {f.news_category !== newsSuggestion.category && (
                      <button
                        type="button"
                        onClick={() => setField("news_category")(newsSuggestion.category)}
                        className="mt-1.5 text-invest underline hover:no-underline"
                        data-testid="news-suggestion-apply"
                      >Appliquer la suggestion</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <Button
          onClick={save}
          disabled={saving || !f.symbol}
          className="w-full h-11 btn-primary font-medium"
          data-testid="save-manual-btn"
        >
          {saving ? "Enregistrement…" : "Enregistrer l'action"}
        </Button>
      </div>

      {/* RIGHT: sticky live score */}
      <div>
        <div className="sticky top-24 bg-card-1 border border-line rounded-xl p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-2">Aperçu du score</div>
          <div className="font-mono-num text-6xl font-bold" style={{ color: decColor }} data-testid="preview-total">
            {preview.total}
          </div>
          <div className="text-sm font-medium" style={{ color: decColor }}>{dec.label}</div>
          <div className="pt-4 border-t border-line space-y-1.5">
            {Object.entries(preview.breakdown).map(([k, b]) => (
              <div key={k} className="flex items-center justify-between text-xs">
                <span className="text-muted-1">{b.label}</span>
                <span className="font-mono-num">{b.points}/{b.max}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Exportable panel — reused inline on Dashboard AND on the /add route
// -----------------------------------------------------------------------------

export function AddStockPanel({ onSaved, initialMode = "single" }) {
  const [mode, setMode] = useState(initialMode);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <div className="inline-flex items-center gap-1 bg-card-1 border border-line rounded-full p-1">
          <button
            onClick={() => setMode("single")}
            className={`px-3 sm:px-4 py-2 text-xs rounded-full transition-colors ${
              mode === "single" ? "btn-primary" : "text-muted-1 hover:text-app"
            }`}
            data-testid="mode-single"
          >Action individuelle</button>
          <button
            onClick={() => setMode("bulk")}
            className={`px-3 sm:px-4 py-2 text-xs rounded-full transition-colors ${
              mode === "bulk" ? "btn-primary" : "text-muted-1 hover:text-app"
            }`}
            data-testid="mode-bulk"
          >Import multi-images</button>
        </div>
      </div>
      {mode === "single"
        ? <SingleStockForm onSaved={onSaved} />
        : <BatchImport onSaved={onSaved} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Page — segmented control
// -----------------------------------------------------------------------------
export default function AddStock() {
  const nav = useNavigate();
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-2 mb-1">Nouvelle entrée</div>
        <h1 className="font-display text-3xl font-semibold">Ajouter des actions</h1>
      </div>
      <AddStockPanel onSaved={() => nav("/")} />
    </div>
  );
}
